import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

const getDefaultFilterValues = (filtersConfig) => Object.fromEntries(
  Object.entries(filtersConfig).map(([key, filterConfig]) => [key, filterConfig.default])
);

const getSerializedFilterValue = (filterConfig, value) => {
  if (typeof filterConfig.serialize === 'function') {
    return filterConfig.serialize(value);
  }

  if (filterConfig.type === 'search') {
    const normalizedValue = typeof value === 'string' ? value.trim() : '';
    return normalizedValue || undefined;
  }

  if (value === filterConfig.default) {
    return undefined;
  }

  return value;
};

const isValidSelectValue = (filterConfig, value) => (
  value === filterConfig.default ||
  filterConfig.options?.some((option) => option.value === value)
);

const getFallbackFilterValue = (filterConfig, fallbackValue) => {
  if (filterConfig.type === 'search') {
    return typeof fallbackValue === 'string' ? fallbackValue : filterConfig.default;
  }

  return isValidSelectValue(filterConfig, fallbackValue)
    ? fallbackValue
    : filterConfig.default;
};

const resolveSelectUrlValue = (filterConfig, rawValue) => {
  if (typeof rawValue !== 'string') {
    return undefined;
  }

  const normalizedValue = rawValue.trim();

  if (!normalizedValue) {
    return undefined;
  }

  const candidateValues = [
    filterConfig.default,
    ...(filterConfig.options?.map((option) => option.value) || []),
  ];

  for (const candidateValue of candidateValues) {
    if (normalizedValue === candidateValue) {
      return candidateValue;
    }

    if (
      typeof filterConfig.serialize === 'function' &&
      filterConfig.serialize(candidateValue) === normalizedValue
    ) {
      return candidateValue;
    }
  }

  return undefined;
};

const parseUrlFilterValue = (filterConfig, rawValue, fallbackValue) => {
  if (filterConfig.type === 'search') {
    if (typeof rawValue !== 'string') {
      return { value: fallbackValue, fromUrl: false };
    }

    const normalizedValue = rawValue.trim();

    if (!normalizedValue) {
      return { value: fallbackValue, fromUrl: false };
    }

    return { value: normalizedValue, fromUrl: true };
  }

  const resolvedValue = resolveSelectUrlValue(filterConfig, rawValue);

  if (!resolvedValue) {
    return { value: fallbackValue, fromUrl: false };
  }

  return { value: resolvedValue, fromUrl: true };
};

const parsePageIndex = (rawPage) => {
  const parsedPage = Number.parseInt(rawPage ?? '', 10);

  if (!Number.isInteger(parsedPage) || parsedPage < 1) {
    return { value: 0, fromUrl: false };
  }

  return { value: parsedPage - 1, fromUrl: true };
};

const buildFetchParams = ({ filtersConfig, filterValues, pageIndex, pagination }) => {
  const params = pagination
    ? {
      page: pageIndex + 1,
      limit: pagination.pageSize,
    }
    : {};

  for (const [key, filterConfig] of Object.entries(filtersConfig)) {
    const serializedValue = getSerializedFilterValue(filterConfig, filterValues[key]);

    if (serializedValue !== undefined) {
      params[filterConfig.urlKey] = serializedValue;
    }
  }

  return params;
};

const resolveInitialState = ({
  locationSearch,
  filtersConfig,
  reduxFilters,
  pagination,
  useReduxFallback,
}) => {
  const params = new URLSearchParams(locationSearch);
  const filterValues = {};
  const hasAnyFilterParam = Object.values(filtersConfig).some((filterConfig) => (
    params.has(filterConfig.urlKey)
  ));
  let shouldSyncFiltersToUrl = false;

  for (const [key, filterConfig] of Object.entries(filtersConfig)) {
    const shouldUseReduxFallback = useReduxFallback && (
      !hasAnyFilterParam || filterConfig.preserveReduxFallback
    );
    const fallbackValue = getFallbackFilterValue(
      filterConfig,
      shouldUseReduxFallback ? reduxFilters?.[key] : undefined
    );
    const parsedFilter = parseUrlFilterValue(filterConfig, params.get(filterConfig.urlKey), fallbackValue);

    filterValues[key] = parsedFilter.value;

    if (parsedFilter.fromUrl) {
      shouldSyncFiltersToUrl = true;
    }
  }

  const parsedPage = pagination ? parsePageIndex(params.get('page')) : { value: 0, fromUrl: false };

  return {
    filterValues,
    pageIndex: parsedPage.value,
    shouldSyncFiltersToUrl: shouldSyncFiltersToUrl || parsedPage.fromUrl,
  };
};

export default function useFilterBar({
  route,
  filters,
  pagination,
  fetchThunk,
  setFilters,
  selectFilters,
  selectLoading,
  selectInitialFetchDone,
  useReduxFallback = true,
  syncResolvedFiltersOnMount = true,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const loading = useSelector(selectLoading);
  const initialFetchDone = useSelector(selectInitialFetchDone);
  const reduxFilters = useSelector(selectFilters || (() => ({})));
  const [state, setState] = useState(() => resolveInitialState({
    locationSearch: location.search,
    filtersConfig: filters,
    reduxFilters,
    pagination,
    useReduxFallback,
  }));
  const stateRef = useRef(state);
  const didInitializeRef = useRef(false);
  const defaultFilterValues = useMemo(() => getDefaultFilterValues(filters), [filters]);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const dispatchFilters = useCallback((nextFilterValues) => {
    if (!setFilters) {
      return;
    }

    dispatch(setFilters(nextFilterValues));
  }, [dispatch, setFilters]);

  const fetchList = useCallback((nextFilterValues, nextPageIndex) => {
    dispatch(fetchThunk(buildFetchParams({
      filtersConfig: filters,
      filterValues: nextFilterValues,
      pageIndex: nextPageIndex,
      pagination,
    })));
  }, [dispatch, fetchThunk, filters, pagination]);

  const syncUrl = useCallback((nextFilterValues, nextPageIndex, shouldSyncFiltersToUrl) => {
    if (!shouldSyncFiltersToUrl) {
      return;
    }

    const params = new URLSearchParams(location.search);

    for (const [key, filterConfig] of Object.entries(filters)) {
      const serializedValue = getSerializedFilterValue(filterConfig, nextFilterValues[key]);

      if (serializedValue === undefined) {
        params.delete(filterConfig.urlKey);
      } else {
        params.set(filterConfig.urlKey, serializedValue);
      }
    }

    if (pagination) {
      params.set('page', String(nextPageIndex + 1));
    } else {
      params.delete('page');
    }

    const nextSearch = params.toString();
    const currentSearch = location.search.startsWith('?')
      ? location.search.slice(1)
      : location.search;

    if (nextSearch === currentSearch) {
      return;
    }

    navigate(nextSearch ? `${route}?${nextSearch}` : route, { replace: true });
  }, [filters, location.search, navigate, pagination, route]);

  useEffect(() => {
    if (didInitializeRef.current) {
      return;
    }

    didInitializeRef.current = true;

    if (syncResolvedFiltersOnMount || state.shouldSyncFiltersToUrl) {
      dispatchFilters(state.filterValues);
    }

    fetchList(state.filterValues, state.pageIndex);
  }, [
    dispatchFilters,
    fetchList,
    state.filterValues,
    state.pageIndex,
    state.shouldSyncFiltersToUrl,
    syncResolvedFiltersOnMount,
  ]);

  const applyFilters = useCallback((partial) => {
    const currentState = stateRef.current;
    const nextFilterValues = { ...currentState.filterValues, ...partial };
    const didAnyFilterChange = Object.keys(partial).some(
      (key) => nextFilterValues[key] !== currentState.filterValues[key]
    );
    const nextPageIndex = pagination && didAnyFilterChange ? 0 : currentState.pageIndex;
    const nextState = {
      ...currentState,
      filterValues: nextFilterValues,
      pageIndex: nextPageIndex,
    };

    stateRef.current = nextState;
    setState(nextState);
    dispatchFilters(nextFilterValues);
    syncUrl(nextFilterValues, nextPageIndex, currentState.shouldSyncFiltersToUrl);
    fetchList(nextFilterValues, nextPageIndex);
  }, [dispatchFilters, fetchList, pagination, syncUrl]);

  const resetFilters = useCallback(() => {
    const currentState = stateRef.current;
    const nextPageIndex = 0;

    const nextState = {
      ...currentState,
      filterValues: defaultFilterValues,
      pageIndex: nextPageIndex,
    };

    stateRef.current = nextState;
    setState(nextState);
    dispatchFilters(defaultFilterValues);
    syncUrl(defaultFilterValues, nextPageIndex, currentState.shouldSyncFiltersToUrl);
    fetchList(defaultFilterValues, nextPageIndex);
  }, [defaultFilterValues, dispatchFilters, fetchList, syncUrl]);

  const handlePageChange = useCallback((nextPageIndex) => {
    if (!pagination) {
      return;
    }

    const currentState = stateRef.current;
    const nextState = {
      ...currentState,
      pageIndex: nextPageIndex,
    };

    stateRef.current = nextState;
    setState(nextState);
    syncUrl(currentState.filterValues, nextPageIndex, currentState.shouldSyncFiltersToUrl);
    fetchList(currentState.filterValues, nextPageIndex);
  }, [fetchList, pagination, syncUrl]);

  const refetchCurrentPage = useCallback(() => {
    fetchList(state.filterValues, state.pageIndex);
  }, [fetchList, state.filterValues, state.pageIndex]);

  const hasActiveFilters = Object.entries(filters).some(([key, filterConfig]) => (
    state.filterValues[key] !== filterConfig.default
  ));

  return {
    filterValues: state.filterValues,
    pageIndex: state.pageIndex,
    isInitialLoading: loading && !initialFetchDone,
    hasActiveFilters,
    applyFilters,
    resetFilters,
    handlePageChange,
    refetchCurrentPage,
  };
}
